import React from 'react';
import styled from 'styled-components';
import axios from 'axios';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SuccessIcon = styled.div`
  color: var(--color-accent);
  margin-bottom: var(--spacing-md);
  
  svg {
    width: 64px;
    height: 64px;
  }
`;

const Title = styled.h2`
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-lg);
  text-align: center;
`;

const PreviewContainer = styled.div`
  width: 100%;
  background-color: var(--color-background);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  font-family: monospace;
  white-space: pre-wrap;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button`
  text-decoration: none;
  text-align: center;
  border: none;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const ResetButton = styled.button`
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SubtitlePreview = ({ subtitleUrl, videoId, previewText, fileName, handleReset }) => {
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState(null);
  
  // Extract base filename for the download
  const baseFileName = fileName ? fileName.split('.')[0] : 'transcript';
  
  // Handle download using axios to ensure proper file download
  const handleDownload = async (e) => {
    // Prevent any default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!videoId) {
      setDownloadError('Video ID not available');
      console.error('Download failed: videoId is not available');
      return;
    }
    
    console.log('Starting download for video ID:', videoId);
    setDownloading(true);
    setDownloadError(null);
    
    try {
      const downloadUrl = `/api/download/${videoId}/`;
      console.log('Download URL:', downloadUrl);
      
      const response = await axios.get(downloadUrl, {
        responseType: 'blob', // Important for file downloads
      });
      
      console.log('Download response received:', {
        status: response.status,
        size: response.data.size,
        type: response.data.type,
        headers: response.headers,
      });
      
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data;
      
      // Verify blob is not empty
      if (blob.size === 0) {
        console.error('Downloaded blob is empty!');
        setDownloadError('Downloaded file is empty. Please check if transcript file was generated correctly.');
        setDownloading(false);
        return;
      }
      
      console.log('Blob size:', blob.size, 'bytes');
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${baseFileName}.txt`);
      link.setAttribute('target', '_blank'); // Some browsers need this
      link.style.display = 'none'; // Hide the link
      document.body.appendChild(link);
      
      console.log('Triggering download for:', `${baseFileName}.txt`);
      
      // Trigger download
      link.click();
      
      // Clean up immediately after click
      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
        console.log('Download cleanup completed');
      }, 100);
      
      setDownloading(false);
      console.log('Download completed successfully');
    } catch (err) {
      setDownloading(false);
      let errorMessage = 'Failed to download subtitle file.';
      
      if (err.response) {
        // When responseType is 'blob', error responses are also blobs
        // Try to parse as JSON if possible
        if (err.response.data instanceof Blob) {
          // Try to read the blob as text and parse as JSON
          try {
            const text = await err.response.data.text();
            const jsonData = JSON.parse(text);
            if (jsonData.error) {
              errorMessage = jsonData.error;
            } else if (jsonData.detail) {
              errorMessage = jsonData.detail;
            }
          } catch (parseErr) {
            // If parsing fails, use status-based message
            if (err.response.status === 404) {
              errorMessage = 'Transcript file not found.';
            } else if (err.response.status === 500) {
              errorMessage = 'Server error occurred while downloading file.';
            } else {
              errorMessage = `Download failed with status ${err.response.status}.`;
            }
          }
        } else {
          // Regular JSON error response
          const data = err.response.data;
          if (data && data.error) {
            errorMessage = data.error;
          } else if (err.response.status === 404) {
            errorMessage = 'Subtitle file not found.';
          }
        }
      } else if (err.request) {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        errorMessage = `Cannot connect to server. Please make sure the backend server is running on ${backendUrl}`;
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setDownloadError(errorMessage);
      console.error('Download error:', err);
    }
  };
  
  return (
    <Container>
      <SuccessIcon>
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M32 58.6667C46.7276 58.6667 58.6667 46.7276 58.6667 32C58.6667 17.2724 46.7276 5.33334 32 5.33334C17.2724 5.33334 5.33337 17.2724 5.33337 32C5.33337 46.7276 17.2724 58.6667 32 58.6667Z" 
            fill="rgba(52, 199, 89, 0.1)" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21.3334 32L28 38.6667L42.6667 24" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </SuccessIcon>
      
      <Title>Subtitles Generated!</Title>
      <Subtitle>Your English subtitles are ready to download.</Subtitle>
      
      <PreviewContainer>
        {previewText}
      </PreviewContainer>
      
      {downloadError && (
        <div style={{
          color: 'var(--color-error)',
          backgroundColor: 'rgba(255, 59, 48, 0.1)',
          padding: 'var(--spacing-md)',
          borderRadius: 'var(--border-radius-sm)',
          marginBottom: 'var(--spacing-md)',
          width: '100%',
          textAlign: 'center',
        }}>
          {downloadError}
        </div>
      )}
      
      <ButtonContainer>
        <Button 
          onClick={handleDownload}
          disabled={downloading || !videoId}
          className="btn btn-primary"
        >
          {downloading ? 'Downloading...' : 'Download Transcript'}
        </Button>
        
        <ResetButton 
          onClick={handleReset}
          className="btn btn-secondary"
        >
          Generate Another
        </ResetButton>
      </ButtonContainer>
    </Container>
  );
};

export default SubtitlePreview;